import React, { useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import CustomDialog from '../../../common/components/CustomDialog';
import { CSVReader } from 'react-papaparse';
import ConfirmationButtons from '../../../components/ConfirmationButtons/ConfirmationButtons';
import { CustomDialogProps } from '../CustomDialog/CustomDialog';
import { sameArrays } from '../../../utils/user';
import SampleFileDownloader from './SampleFileDownloader';
import Alert from '@material-ui/lab/Alert';
import { PRIMARY } from '../../../constants/colors';

export interface IRawCSV<T> {
  data: T;
}

interface ICSVImportation<T> extends CustomDialogProps {
  headers: string[];
  samples?: { [p: string]: string }[];
  onImport: (data: IRawCSV<T>[]) => void;
}

interface IError {
  value: boolean;
  message: string;
}

export enum FileValidation {
  VALID = 'VALID',
  EMPTY = 'EMPTY',
  INVALID = 'INVALID',
}

export const validateCSVHeaders = (
  csv: IRawCSV<any>[],
  headers: string[],
): FileValidation => {
  if (!csv?.length) return FileValidation.EMPTY;
  if (csv[0] && !sameArrays(headers, Object.keys(csv[0].data)))
    return FileValidation.INVALID;
  return FileValidation.VALID;
};

const CSVImportation = <T extends Object>(
  props: ICSVImportation<T> & CustomDialogProps,
) => {
  const {
    headers,
    samples,
    onImport,
    onClose,
    loading,
    title,
    ...other
  } = props;
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState<IRawCSV<T>[]>([]);
  const [error, setError] = useState<IError>({ value: false, message: '' });

  const _samples = useMemo(
    () => [
      headers.reduce<{ [p: string]: string }>(
        (acc, h) => ({
          ...acc,
          [h]: '',
        }),
        Object.assign({}),
      ),
    ],
    [headers],
  );

  const handleFileLoad = (data: any[]) => {
    const status = validateCSVHeaders(data, headers);

    let error: IError = { value: false, message: '' };

    switch (status) {
      case FileValidation.VALID:
        setData(data);
        setLoaded(true);
        break;

      case FileValidation.EMPTY:
        error = {
          value: true,
          message: 'Fichier vide.',
        };
        break;

      case FileValidation.INVALID:
        error = {
          value: true,
          message: 'Fichier invalide. Vérifier les en-têtes.',
        };
    }

    setError(error);
  };

  const handleOnError = (err: any, file: any, inputElem: any, reason: any) => {
    setData([]);
    setLoaded(false);
    console.log('Import error');
  };

  const handleOnRemoveFile = () => {
    setData([]);
    setLoaded(false);
    setError({ value: false, message: '' });
  };

  return (
    <CustomDialog
      fullWidth={false}
      maxWidth="sm"
      loading={loading}
      actions={
        <ConfirmationButtons
          handleCancel={onClose}
          handleConfirm={() => onImport(data)}
          disableConfirm={!loaded || loading || error.value}
        />
      }
      title={
        <SampleFileDownloader data={samples ?? _samples}>
          {title}
        </SampleFileDownloader>
      }
      {...other}>
      {error.value && (
        <Box mb={1}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      )}
      <CSVReader
        onError={handleOnError}
        addRemoveButton
        removeButtonColor="#ff4338"
        progressBarColor="#ff4338"
        onFileLoad={handleFileLoad}
        onRemoveFile={handleOnRemoveFile}
        style={{
          dropArea: {
            borderColor: PRIMARY,
            height: 150,
          },
        }}
        config={{
          header: true,
          skipEmptyLines: true,
          fastMode: true,
        }}>
        <Box>Déposer le fichier CSV ici ou cliquer pour parcourir.</Box>
      </CSVReader>
    </CustomDialog>
  );
};

export default CSVImportation;
